import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { usePageSEO } from '../hooks/usePageSEO';
import { MarketingHeader, MarketingFooter } from './OtherPages';
import {
  ArrowRight, Mail, Linkedin, FileText, Target, Star, Zap, Sparkles,
  Users, ShieldCheck, GraduationCap, Mic, Trophy, Lightbulb, Globe, TrendingUp,
  Eye, Heart, Briefcase, ClipboardList, Cpu, PenTool, Code2, LifeBuoy, Megaphone,
  Lock, FileCheck, CreditCard, MessageCircle, CheckCircle2, Clock,
} from 'lucide-react';

/* ═══════════════════════════
   DATA
═══════════════════════════ */
const JOURNEY_STEPS = [
  { label: 'Student', Icon: GraduationCap },
  { label: 'Resume', Icon: FileText },
  { label: 'ATS Check', Icon: Target },
  { label: 'Interview', Icon: Mic },
  { label: 'Job Offer', Icon: Trophy },
];

const STORY_MILESTONES = [
  { year: '2024', Icon: Lightbulb, title: 'The idea was born', desc: 'After seeing talented people rejected because of weak resumes — not weak careers.' },
  { year: null, Icon: FileText, title: 'Built the first AI Resume Builder', desc: 'An AI that turns plain-language experience into a complete, ATS-ready resume in seconds.' },
  { year: null, Icon: Target, title: 'Added the ATS Checker', desc: 'A real scoring engine that shows exactly what applicant tracking software sees — and what to fix.' },
  { year: null, Icon: Mic, title: 'Added AI Interview Practice', desc: 'On-camera mock interviews with real-time AI feedback, so the resume isn’t the only thing ready.' },
  { year: 'Today', Icon: Globe, title: 'Helping professionals worldwide', desc: 'From first job to next promotion, MarsResume is part of the job search in dozens of countries.' },
];

const PROBLEMS = [
  'Most resumes fail ATS.',
  'People don’t know how recruiters think.',
  'Career coaching is expensive.',
  'Every application requires customization.',
];
const SOLUTIONS = [
  { label: 'AI Resume Builder', Icon: Sparkles },
  { label: 'ATS Checker', Icon: Target },
  { label: 'Job Tailoring', Icon: Briefcase },
  { label: 'Interview Practice', Icon: Mic },
  { label: 'Cover Letter', Icon: Mail },
  { label: 'LinkedIn Optimizer', Icon: Linkedin },
];

const MISSION_CARDS = [
  { Icon: Globe, title: 'Make Career Growth Accessible', desc: 'AI should help everyone.' },
  { Icon: Zap, title: 'Help People Save Time', desc: 'Build resumes in minutes.' },
  { Icon: TrendingUp, title: 'Increase Interview Success', desc: 'Better resumes. Better careers.' },
];

const VALUES = [
  { Icon: ShieldCheck, title: 'Trust', desc: 'Everything built around recruiter best practices.' },
  { Icon: Sparkles, title: 'Innovation', desc: 'Constantly improving AI.' },
  { Icon: Eye, title: 'Transparency', desc: 'Clear pricing.' },
  { Icon: Heart, title: 'User First', desc: 'Every feature solves a real hiring problem.' },
];

const ECOSYSTEM = [
  { label: 'Resume Builder', Icon: FileText, path: '/build/resume' },
  { label: 'ATS Checker', Icon: Target, path: '/ats-checker' },
  { label: 'Job Tailoring', Icon: Briefcase, path: '/signup' },
  { label: 'Cover Letter', Icon: Mail, path: '/build/cover_letter' },
  { label: 'LinkedIn Optimizer', Icon: Linkedin, path: '/signup' },
  { label: 'Interview Practice', Icon: Mic, path: '/ai-video-interview' },
  { label: 'Job Tracker', Icon: ClipboardList, path: '/signup' },
];

const STATS = [
  { value: 1000, decimals: 0, suffix: '+', label: 'Professionals Helped', Icon: Users },
  { value: 500, decimals: 0, suffix: '+', label: 'Resumes Created', Icon: FileText },
  { value: 94, decimals: 0, suffix: '%', label: 'Average ATS Score', Icon: Target },
  { value: 25, decimals: 0, suffix: '+', label: 'Countries', Icon: Globe },
  { value: 4.9, decimals: 1, suffix: '★', label: 'User Rating', Icon: Star },
];

const DEPARTMENTS = [
  { label: 'AI Engineering', Icon: Cpu, desc: 'Building the AI that reads resumes like a recruiter.' },
  { label: 'Product Design', Icon: PenTool, desc: 'Designing every screen to feel effortless.' },
  { label: 'Software Engineering', Icon: Code2, desc: 'Shipping a platform that just works.' },
  { label: 'Customer Success', Icon: LifeBuoy, desc: 'Here whenever you need help.' },
  { label: 'Marketing', Icon: Megaphone, desc: 'Telling the MarsResume story.' },
];

const TRUST_ITEMS = [
  { label: 'Secure platform', Icon: ShieldCheck },
  { label: 'Privacy-first', Icon: Lock },
  { label: 'ATS-friendly templates', Icon: FileCheck },
  { label: 'AI powered', Icon: Sparkles },
  { label: 'No credit card to start', Icon: CreditCard },
  { label: 'Fast support', Icon: MessageCircle },
];

const ROADMAP_NOW = [
  { label: 'Resume Builder', Icon: FileText },
  { label: 'ATS Checker', Icon: Target },
  { label: 'Interview AI', Icon: Mic },
];
const ROADMAP_NEXT = [
  { label: 'Career Coach', Icon: Users },
  { label: 'Job Match AI', Icon: Sparkles },
  { label: 'Salary Insights', Icon: TrendingUp },
  { label: 'Application Analytics', Icon: ClipboardList },
];

const FAQS = [
  ['Who built MarsResume?', 'MarsResume was founded by Malik Hassan and is built by a small, focused team at Devops Markaz Tech Pvt. Ltd. — real people shipping a product they use themselves.'],
  ['Is MarsResume free?', 'Yes. You can build a resume, run an ATS check, and explore the platform without a credit card. Premium features are available if you want more.'],
  ['What makes MarsResume different?', 'Every tool is connected — resume, ATS score, tailoring, cover letter, and interview practice all work from the same profile, instead of five disconnected apps.'],
  ['Is my data secure?', 'Your resume data is encrypted and never sold. You own it, and you can delete it at any time.'],
  ['How accurate is the ATS Checker?', 'It’s built around the same parsing patterns real applicant tracking systems use — formatting, keyword matching, and section structure — so the score reflects what a recruiter’s software actually sees.'],
  ['Can I export PDFs?', 'Yes. Every resume and cover letter exports to a clean, ATS-safe PDF in one click, with Word export also available.'],
  ['How does AI work?', 'You describe your experience in plain language, and MarsResume’s AI structures it into polished, quantified bullet points and tailors them to the job you’re applying for.'],
  ['Do recruiters use ATS?', 'Yes — an estimated 98% of Fortune 500 companies and most mid-sized employers use an ATS to screen resumes before a human ever sees them.'],
];

/* ═══════════════════════════
   SHARED PIECES
═══════════════════════════ */
const SectionHeading = ({ eyebrow, title, subtitle }) => (
  <div className="ab-sec-head">
    {eyebrow && <div className="ab-sec-eyebrow">{eyebrow}</div>}
    <h2>{title}</h2>
    {subtitle && <p>{subtitle}</p>}
  </div>
);

const AnimatedCounter = ({ value, decimals = 0, duration = 1400, suffix = '' }) => {
  const ref = useRef(null);
  const startedRef = useRef(false);
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !startedRef.current) {
        startedRef.current = true;
        const start = performance.now();
        const step = (now) => {
          const progress = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setDisplay(value * eased);
          if (progress < 1) requestAnimationFrame(step);
          else setDisplay(value);
        };
        requestAnimationFrame(step);
        observer.disconnect();
      }
    }, { threshold: 0.4 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [value, duration]);

  return <span ref={ref}>{display.toFixed(decimals)}{suffix}</span>;
};

/* ═══════════════════════════
   HERO
═══════════════════════════ */
const HeroSection = ({ goTo, onMission }) => (
  <section className="ab-hero">
    <div className="ab-hero-grid-bg" />
    <div className="ab-hero-glow" />
    <div className="ab-hero-inner">
      <div className="ab-hero-left">
        <div className="ab-hero-badge"><span className="ab-hero-badge-dot" /> Our Story</div>
        <h1>Helping People Get Hired Faster.</h1>
        <p>MarsResume was built because job seekers deserve better tools—not expensive career coaches. Our mission is to make AI-powered career growth accessible to everyone.</p>
        <div className="ab-hero-ctas">
          <button type="button" className="ab-btn-primary" style={{ padding: '14px 26px', fontSize: 15 }} onClick={() => goTo('/signup')}>Try MarsResume Free <ArrowRight size={16} /></button>
          <button type="button" className="ab-btn-ghost-dark" onClick={onMission}>Our Mission</button>
        </div>
      </div>
      <div className="ab-hero-right">
        <div className="ab-journey-card">
          <div className="ab-journey-head">The Journey</div>
          {JOURNEY_STEPS.map((step, i) => (
            <React.Fragment key={step.label}>
              <div className="ab-journey-step">
                <div className="ab-journey-icon"><step.Icon size={17} /></div>
                <div className="ab-journey-label">{step.label}</div>
              </div>
              {i < JOURNEY_STEPS.length - 1 && (
                <div className="ab-journey-connector">
                  <img src="/assets/mars-logo.png" alt="" className="ab-journey-logo-dot" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ═══════════════════════════
   OUR STORY
═══════════════════════════ */
const StorySection = () => (
  <section className="ab-section">
    <SectionHeading title="How MarsResume Started" />
    <div className="ab-timeline">
      {STORY_MILESTONES.map((m, i) => (
        <motion.div key={m.title} className="ab-timeline-item" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.4, delay: i * 0.05 }}>
          <div className="ab-timeline-icon"><m.Icon size={22} /></div>
          <div className="ab-timeline-body">
            {m.year && <div className="ab-timeline-year">{m.year}</div>}
            <div className="ab-timeline-title">{m.title}</div>
            <p className="ab-timeline-desc">{m.desc}</p>
          </div>
        </motion.div>
      ))}
    </div>
  </section>
);

/* ═══════════════════════════
   WHY WE BUILT THIS
═══════════════════════════ */
const WhyBuiltSection = () => (
  <section className="ab-section ab-section-tint">
    <SectionHeading title="Why We Built This" subtitle="Job hunting was broken. Here's what we set out to fix." />
    <div className="ab-why-grid">
      <div className="ab-why-card ab-why-problems">
        <div className="ab-why-card-head">The Problems</div>
        <div className="ab-why-list">
          {PROBLEMS.map(p => (
            <div key={p} className="ab-why-problem-row">
              <span className="ab-why-problem-dot" />
              {p}
            </div>
          ))}
        </div>
      </div>
      <div className="ab-why-card ab-why-solutions">
        <div className="ab-why-card-head">How MarsResume Solves It</div>
        <div className="ab-why-solutions-grid">
          {SOLUTIONS.map(s => (
            <div key={s.label} className="ab-why-solution-row">
              <span className="ab-why-solution-icon"><s.Icon size={15} /></span>
              {s.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ═══════════════════════════
   OUR MISSION (3 cards)
═══════════════════════════ */
const MissionSection = ({ missionRef }) => (
  <section className="ab-section" ref={missionRef}>
    <SectionHeading title="Our Mission" />
    <div className="ab-mission-grid">
      {MISSION_CARDS.map(card => (
        <div key={card.title} className="ab-mission-card">
          <div className="ab-mission-icon"><card.Icon size={26} /></div>
          <div className="ab-mission-title">{card.title}</div>
          <p className="ab-mission-desc">{card.desc}</p>
        </div>
      ))}
    </div>
  </section>
);

/* ═══════════════════════════
   OUR VALUES (4 cards)
═══════════════════════════ */
const ValuesSection = () => (
  <section className="ab-section ab-section-tint">
    <SectionHeading title="Our Values" subtitle="What drives every decision we make." />
    <div className="ab-values-grid">
      {VALUES.map(v => (
        <div key={v.title} className="ab-value-card">
          <div className="ab-value-icon"><v.Icon size={26} /></div>
          <div className="ab-value-title">{v.title}</div>
          <p className="ab-value-desc">{v.desc}</p>
        </div>
      ))}
    </div>
  </section>
);

/* ═══════════════════════════
   PRODUCT ECOSYSTEM
═══════════════════════════ */
const EcosystemSection = ({ goTo }) => (
  <section className="ab-section">
    <SectionHeading title="Everything You Need in One Place" subtitle="Not isolated tools — one connected career journey." />
    <div className="ab-eco-scroll">
      <div className="ab-eco-track">
        {ECOSYSTEM.map((step, i) => (
          <React.Fragment key={step.label}>
            <button type="button" className="ab-eco-card" onClick={() => goTo(step.path)}>
              <div className="ab-eco-icon"><step.Icon size={20} /></div>
              <div className="ab-eco-label">{step.label}</div>
            </button>
            {i < ECOSYSTEM.length - 1 && <div className="ab-eco-arrow"><ArrowRight size={14} /></div>}
          </React.Fragment>
        ))}
      </div>
    </div>
  </section>
);

/* ═══════════════════════════
   BY THE NUMBERS
═══════════════════════════ */
const StatsSection = () => (
  <section className="ab-stats-outer">
    <div className="ab-stats-inner">
      <SectionHeading title="By the Numbers" />
      <div className="ab-stats-grid">
        {STATS.map(s => (
          <div key={s.label} className="ab-num-stat-card">
            <div className="ab-num-stat-icon"><s.Icon size={20} /></div>
            <div className="ab-num-stat-value"><AnimatedCounter value={s.value} decimals={s.decimals} suffix={s.suffix} /></div>
            <div className="ab-num-stat-label">{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ═══════════════════════════
   MEET THE FOUNDER
═══════════════════════════ */
const FounderSection = ({ navigate }) => (
  <section className="ab-section">
    <SectionHeading title="Meet the Founder" />
    <div className="ab-founder-card">
      <div className="ab-founder-photo">
        <img src="/assets/malik-hassan-sample.png" alt="Malik Hassan, Founder of MarsResume" />
      </div>
      <div className="ab-founder-body">
        <div className="ab-founder-name">Malik Hassan</div>
        <div className="ab-founder-role">Founder</div>
        <p className="ab-founder-quote">
          <span className="ab-founder-quote-mark">&ldquo;</span>
          I started MarsResume after seeing talented people struggle to get interviews because their resumes weren&apos;t ATS-friendly. I wanted to build an AI platform that gives everyone access to professional career tools.
        </p>
        <div className="ab-founder-ctas">
          <a href="https://www.linkedin.com/company/123194112/admin/dashboard/" target="_blank" rel="noopener noreferrer" className="ab-btn-secondary"><Linkedin size={15} /> LinkedIn</a>
          <button type="button" className="ab-btn-secondary" onClick={() => navigate('/contact')}><Mail size={15} /> Contact</button>
        </div>
      </div>
    </div>
  </section>
);

/* ═══════════════════════════
   THE TEAM (departments)
═══════════════════════════ */
const TeamSection = () => (
  <section className="ab-section ab-section-tint">
    <SectionHeading title="The Team" subtitle="Real people, organized around what it takes to get you hired." />
    <div className="ab-dept-grid">
      {DEPARTMENTS.map(d => (
        <div key={d.label} className="ab-dept-card">
          <div className="ab-dept-icon"><d.Icon size={20} /></div>
          <div className="ab-dept-label">{d.label}</div>
          <p className="ab-dept-desc">{d.desc}</p>
        </div>
      ))}
    </div>
  </section>
);

/* ═══════════════════════════
   WHY PEOPLE TRUST US
═══════════════════════════ */
const TrustSection = () => (
  <section className="ab-section">
    <SectionHeading title="Why People Trust Us" />
    <div className="ab-trust-grid">
      {TRUST_ITEMS.map(t => (
        <div key={t.label} className="ab-trust-card">
          <div className="ab-trust-icon"><t.Icon size={18} /></div>
          {t.label}
        </div>
      ))}
    </div>
  </section>
);

/* ═══════════════════════════
   WHAT'S NEXT
═══════════════════════════ */
const RoadmapSection = () => (
  <section className="ab-section ab-section-tint">
    <SectionHeading title="What's Next" subtitle="MarsResume today, and what we're building next." />
    <div className="ab-roadmap-grid">
      <div className="ab-roadmap-col">
        <div className="ab-roadmap-col-head ab-roadmap-now-head"><CheckCircle2 size={16} /> Currently</div>
        {ROADMAP_NOW.map(item => (
          <div key={item.label} className="ab-roadmap-row ab-roadmap-row-now">
            <span className="ab-roadmap-row-icon"><item.Icon size={15} /></span>
            {item.label}
            <span className="ab-roadmap-live-badge">Live</span>
          </div>
        ))}
      </div>
      <div className="ab-roadmap-col">
        <div className="ab-roadmap-col-head ab-roadmap-next-head"><Clock size={16} /> Coming Soon</div>
        {ROADMAP_NEXT.map(item => (
          <div key={item.label} className="ab-roadmap-row ab-roadmap-row-next">
            <span className="ab-roadmap-row-icon"><item.Icon size={15} /></span>
            {item.label}
            <span className="ab-roadmap-soon-badge">Soon</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ═══════════════════════════
   FAQ
═══════════════════════════ */
const FAQSection = () => {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <section className="ab-section">
      <SectionHeading title="Frequently Asked Questions" />
      <div className="ab-faq-list">
        {FAQS.map(([q, a], i) => {
          const isOpen = openIdx === i;
          return (
            <div key={q} className={`ab-faq-item${isOpen ? ' open' : ''}`}>
              <button type="button" className="ab-faq-trigger" aria-expanded={isOpen} onClick={() => setOpenIdx(isOpen ? null : i)}>
                <span>{q}</span>
                <span className="ab-faq-icon">
                  <span className="bar h" />
                  <motion.span className="bar v" animate={{ rotate: isOpen ? 90 : 0 }} transition={{ duration: 0.25, ease: 'easeInOut' }} />
                </span>
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }} style={{ overflow: 'hidden' }}>
                    <div className="ab-faq-answer">{a}</div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </section>
  );
};

/* ═══════════════════════════
   FINAL CTA
═══════════════════════════ */
const FinalCTASection = ({ goTo, navigate }) => (
  <section className="ab-final-cta-outer">
    <div className="ab-final-cta-card">
      <div className="ab-final-cta-mesh" />
      <div className="ab-final-cta-glow" />
      <div className="ab-final-cta-content">
        <h2>Ready to Build Your Best Resume?</h2>
        <p>Join thousands of professionals using MarsResume to build resumes, improve ATS scores, prepare for interviews, and land better jobs.</p>
        <div className="ab-final-cta-actions">
          <button type="button" className="ab-btn-primary" style={{ padding: '14px 26px', fontSize: 15 }} onClick={() => goTo('/signup')}>Start Free <ArrowRight size={16} /></button>
          <button type="button" className="ab-btn-ghost-dark" onClick={() => navigate('/templates')}>View Templates</button>
        </div>
      </div>
    </div>
  </section>
);

/* ═══════════════════════════
   MAIN PAGE
═══════════════════════════ */
export const AboutPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const missionRef = useRef(null);

  usePageSEO({
    title: 'About Us – MarsResume | Our Story & Mission',
    description: 'MarsResume was built to make AI-powered career tools accessible to everyone. Learn our story, our mission, and the team helping professionals get hired faster.',
    canonical: '/about',
  });

  const goTo = (path) => navigate(user ? (path === '/signup' ? '/dashboard' : path) : path);
  const scrollToMission = () => missionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className="ab-page">
      <AboutStyles />
      <MarketingHeader />

      <HeroSection goTo={goTo} onMission={scrollToMission} />
      <StorySection />
      <WhyBuiltSection />
      <MissionSection missionRef={missionRef} />
      <ValuesSection />
      <EcosystemSection goTo={goTo} />
      <StatsSection />
      <FounderSection navigate={navigate} />
      <TeamSection />
      <TrustSection />
      <RoadmapSection />
      <FAQSection />
      <FinalCTASection goTo={goTo} navigate={navigate} />

      <MarketingFooter navigate={navigate} goTo={goTo} />
    </div>
  );
};

/* ═══════════════════════════
   STYLES
═══════════════════════════ */
const AboutStyles = () => (
  <style>{`
    .ab-page { min-height: 100vh; background: #F9FAFB; font-family: 'Inter', sans-serif; color: #111827; overflow-x: hidden; }

    .ab-btn-primary { background:#EF1D28;color:white;border:none;border-radius:10px;padding:9px 20px;font-weight:700;font-size:13px;cursor:pointer;font-family:inherit;display:inline-flex;align-items:center;gap:7px;transition:all 0.2s;box-shadow:0 2px 8px rgba(239,29,40,0.2); }
    .ab-btn-primary:hover { background:#D91620;transform:translateY(-1px);box-shadow:0 8px 20px rgba(239,29,40,0.3); }
    .ab-btn-ghost-dark { background: rgba(255,255,255,0.06); border: 1.5px solid rgba(255,255,255,0.2); color: white; border-radius: 12px; padding: 14px 26px; font-weight: 700; font-size: 15px; cursor: pointer; font-family: inherit; display: inline-flex; align-items: center; gap: 8px; transition: all 0.2s; }
    .ab-btn-ghost-dark:hover { background: rgba(255,255,255,0.12); border-color: rgba(255,255,255,0.35); transform: translateY(-2px); }
    .ab-btn-secondary { display: inline-flex; align-items: center; gap: 8px; background: white; border: 1.5px solid #E5E7EB; color: #374151; border-radius: 11px; padding: 11px 20px; font-weight: 700; font-size: 13.5px; cursor: pointer; font-family: inherit; text-decoration: none; transition: all 0.2s; }
    .ab-btn-secondary:hover { border-color: #D1D5DB; background: #F9FAFB; transform: translateY(-2px); }

    /* Section shell */
    .ab-section { padding: 76px 48px; max-width: 1180px; margin: 0 auto; }
    .ab-section-tint { background: #FAFBFF; max-width: none; padding-left: 48px; padding-right: 48px; }
    .ab-section-tint > * { max-width: 1180px; margin-left: auto; margin-right: auto; }
    .ab-sec-head { text-align: center; margin-bottom: 48px; }
    .ab-sec-eyebrow { display: inline-flex; align-items: center; gap: 6px; background: #FFF1F2; border: 1px solid #FECACA; color: #EF1D28; border-radius: 20px; padding: 5px 16px; font-size: 11px; font-weight: 700; letter-spacing: 0.8px; text-transform: uppercase; margin-bottom: 16px; }
    .ab-sec-head h2 { margin: 0; font-size: 34px; font-weight: 900; color: #0F172A; letter-spacing: -0.8px; }
    .ab-sec-head p { margin: 12px auto 0; color: #64748B; font-size: 15px; line-height: 1.65; max-width: 580px; }

    /* Hero */
    .ab-hero { background: linear-gradient(160deg,#070E1A 0%,#0D1120 50%,#0A1420 100%); padding: 88px 48px 96px; position: relative; overflow: hidden; }
    .ab-hero-grid-bg { position: absolute; inset: 0; background-image: radial-gradient(rgba(255,255,255,0.06) 1px,transparent 1px); background-size: 28px 28px; pointer-events: none; }
    .ab-hero-glow { position: absolute; top: 10%; left: 15%; width: 700px; height: 420px; background: radial-gradient(ellipse, rgba(239,29,40,0.14) 0%, transparent 70%); pointer-events: none; }
    .ab-hero-inner { max-width: 1180px; margin: 0 auto; display: flex; align-items: center; gap: 56px; position: relative; }
    .ab-hero-left { flex: 0 0 50%; }
    .ab-hero-badge { display: inline-flex; align-items: center; gap: 7px; background: rgba(239,29,40,0.14); border: 1px solid rgba(239,29,40,0.25); border-radius: 24px; padding: 6px 16px; font-size: 11px; font-weight: 700; color: #FCA5A5; letter-spacing: 1px; text-transform: uppercase; margin-bottom: 22px; }
    .ab-hero-badge-dot { width: 6px; height: 6px; border-radius: 50%; background: #EF1D28; }
    .ab-hero-left h1 { margin: 0 0 20px; font-size: 46px; line-height: 1.12; font-weight: 900; letter-spacing: -1.4px; color: white; }
    .ab-hero-left p { margin: 0 0 32px; font-size: 16.5px; line-height: 1.7; color: #94A3B8; max-width: 480px; }
    .ab-hero-ctas { display: flex; gap: 12px; flex-wrap: wrap; }
    .ab-hero-right { flex: 1; display: flex; align-items: center; justify-content: center; }

    .ab-journey-card { width: 300px; background: rgba(255,255,255,0.97); backdrop-filter: blur(18px); border-radius: 20px; box-shadow: 0 40px 90px rgba(0,0,0,0.45); border: 1px solid rgba(255,255,255,0.1); padding: 8px; }
    .ab-journey-head { padding: 12px 14px 10px; font-size: 12px; font-weight: 800; color: #94A3B8; text-transform: uppercase; letter-spacing: 0.6px; }
    .ab-journey-step { display: flex; align-items: center; gap: 12px; padding: 10px 14px; }
    .ab-journey-icon { width: 36px; height: 36px; border-radius: 10px; background: #FFF1F2; color: #EF1D28; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
    .ab-journey-label { font-size: 14px; font-weight: 800; color: #111827; }
    .ab-journey-connector { display: flex; align-items: center; justify-content: flex-start; padding-left: 32px; height: 22px; position: relative; }
    .ab-journey-connector::before { content: ''; position: absolute; left: 17px; top: 0; bottom: 0; width: 2px; background: linear-gradient(180deg,#FBD3D5,#EF1D28); }
    .ab-journey-logo-dot { width: 16px; height: 16px; object-fit: contain; position: relative; z-index: 1; background: white; border-radius: 50%; padding: 1px; box-shadow: 0 0 0 2px white; margin-left: -8px; }

    /* Timeline */
    .ab-timeline { position: relative; max-width: 640px; margin: 0 auto; }
    .ab-timeline::before { content: ''; position: absolute; left: 27px; top: 8px; bottom: 8px; width: 2px; background: linear-gradient(180deg,#FBD3D5,#EF1D28,#FBD3D5); }
    .ab-timeline-item { position: relative; display: flex; gap: 20px; padding-bottom: 40px; }
    .ab-timeline-item:last-child { padding-bottom: 0; }
    .ab-timeline-icon { width: 56px; height: 56px; border-radius: 50%; background: white; border: 2px solid #EF1D28; color: #EF1D28; display: flex; align-items: center; justify-content: center; flex-shrink: 0; z-index: 1; box-shadow: 0 4px 14px rgba(239,29,40,0.15); }
    .ab-timeline-body { padding-top: 8px; }
    .ab-timeline-year { font-size: 11px; font-weight: 800; color: #EF1D28; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 4px; }
    .ab-timeline-title { font-size: 17px; font-weight: 800; color: #111827; margin-bottom: 6px; }
    .ab-timeline-desc { margin: 0; font-size: 13.5px; color: #6B7280; line-height: 1.65; }

    /* Why we built this */
    .ab-why-grid { display: grid; grid-template-columns: 1fr 1.3fr; gap: 24px; max-width: 1000px; margin: 0 auto; align-items: stretch; }
    .ab-why-card { background: white; border: 1px solid #E5E7EB; border-radius: 20px; padding: 28px; }
    .ab-why-card-head { font-size: 13px; font-weight: 800; color: #94A3B8; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 20px; }
    .ab-why-list { display: flex; flex-direction: column; gap: 4px; }
    .ab-why-problem-row { display: flex; align-items: center; gap: 12px; padding: 12px 4px; font-size: 14.5px; font-weight: 600; color: #111827; border-bottom: 1px solid #F3F4F6; }
    .ab-why-problem-row:last-child { border-bottom: none; }
    .ab-why-problem-dot { width: 8px; height: 8px; border-radius: 50%; background: #EF1D28; flex-shrink: 0; }
    .ab-why-solutions-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
    .ab-why-solution-row { display: flex; align-items: center; gap: 10px; background: #FAFBFF; border: 1px solid #F1F5F9; border-radius: 12px; padding: 12px 14px; font-size: 13.5px; font-weight: 700; color: #111827; }
    .ab-why-solution-icon { width: 28px; height: 28px; border-radius: 8px; background: #FFF1F2; color: #EF1D28; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }

    /* Mission cards */
    .ab-mission-grid { display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 24px; }
    .ab-mission-card { background: white; border: 1px solid #E5E7EB; border-radius: 22px; padding: 40px 32px; text-align: center; transition: box-shadow 0.25s ease, transform 0.25s ease, border-color 0.25s ease; }
    .ab-mission-card:hover { box-shadow: 0 20px 48px rgba(15,23,42,0.08); transform: translateY(-4px); border-color: #D1D5DB; }
    .ab-mission-icon { width: 60px; height: 60px; border-radius: 16px; background: linear-gradient(135deg,#FFF1F2,#FFE4E6); color: #EF1D28; display: flex; align-items: center; justify-content: center; margin: 0 auto 22px; }
    .ab-mission-title { font-size: 19px; font-weight: 800; color: #111827; margin-bottom: 10px; letter-spacing: -0.3px; }
    .ab-mission-desc { margin: 0; font-size: 14.5px; color: #6B7280; }

    /* Values */
    .ab-values-grid { display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 22px; }
    .ab-value-card { background: white; border: 1px solid #E5E7EB; border-radius: 20px; padding: 34px 26px; transition: box-shadow 0.25s ease, transform 0.25s ease, border-color 0.25s ease; }
    .ab-value-card:hover { box-shadow: 0 18px 44px rgba(15,23,42,0.08); transform: translateY(-4px); border-color: #D1D5DB; }
    .ab-value-icon { width: 56px; height: 56px; border-radius: 15px; background: #FFF1F2; color: #EF1D28; display: flex; align-items: center; justify-content: center; margin-bottom: 20px; }
    .ab-value-title { font-size: 17px; font-weight: 800; color: #111827; margin-bottom: 8px; }
    .ab-value-desc { margin: 0; font-size: 13.5px; color: #6B7280; line-height: 1.6; }

    /* Ecosystem */
    .ab-eco-scroll { overflow-x: auto; scrollbar-width: none; -ms-overflow-style: none; margin: 0 -48px; padding: 4px 48px 12px; }
    .ab-eco-scroll::-webkit-scrollbar { display: none; }
    .ab-eco-track { display: flex; align-items: center; gap: 0; width: max-content; margin: 0 auto; }
    .ab-eco-card { width: 148px; text-align: center; background: white; border: 1px solid #E5E7EB; border-radius: 18px; padding: 22px 14px; cursor: pointer; font-family: inherit; transition: box-shadow 0.24s ease, transform 0.24s ease, border-color 0.24s ease; }
    .ab-eco-card:hover { box-shadow: 0 16px 40px rgba(15,23,42,0.08); transform: translateY(-4px); border-color: #FBD3D5; }
    .ab-eco-icon { width: 42px; height: 42px; border-radius: 12px; background: linear-gradient(135deg,#EF1D28,#FF6B6B); color: white; display: flex; align-items: center; justify-content: center; margin: 0 auto 12px; box-shadow: 0 6px 16px rgba(239,29,40,0.25); }
    .ab-eco-label { font-size: 12.5px; font-weight: 800; color: #111827; line-height: 1.35; }
    .ab-eco-arrow { width: 32px; display: flex; align-items: center; justify-content: center; color: #FBD3D5; flex-shrink: 0; }

    /* Stats */
    .ab-stats-outer { background: linear-gradient(180deg,#0A1428,#0D1120); padding: 76px 48px; position: relative; overflow: hidden; }
    .ab-stats-outer .ab-sec-head h2 { color: white; }
    .ab-stats-outer .ab-sec-head p { color: #64748B; }
    .ab-stats-inner { max-width: 1180px; margin: 0 auto; position: relative; }
    .ab-stats-grid { display: grid; grid-template-columns: repeat(5, minmax(0,1fr)); gap: 18px; }
    .ab-num-stat-card { background: #0F1A2B; border: 1px solid #1E2D45; border-radius: 18px; padding: 28px 16px; text-align: center; transition: all 0.25s; }
    .ab-num-stat-card:hover { border-color: rgba(239,29,40,0.3); background: #152030; transform: translateY(-3px); }
    .ab-num-stat-icon { width: 40px; height: 40px; border-radius: 12px; background: rgba(239,29,40,0.12); color: #EF1D28; display: flex; align-items: center; justify-content: center; margin: 0 auto 14px; }
    .ab-num-stat-value { font-size: 28px; font-weight: 900; color: white; letter-spacing: -1px; margin-bottom: 6px; }
    .ab-num-stat-label { font-size: 12px; color: #64748B; font-weight: 500; }

    /* Founder */
    .ab-founder-card { max-width: 900px; margin: 0 auto; background: white; border: 1px solid #E5E7EB; border-radius: 26px; padding: 8px; display: flex; align-items: stretch; gap: 0; overflow: hidden; box-shadow: 0 20px 60px rgba(15,23,42,0.06); }
    .ab-founder-photo { flex: 0 0 260px; border-radius: 20px; overflow: hidden; }
    .ab-founder-photo img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .ab-founder-body { flex: 1; padding: 36px 40px; display: flex; flex-direction: column; justify-content: center; }
    .ab-founder-name { font-size: 22px; font-weight: 900; color: #111827; letter-spacing: -0.4px; }
    .ab-founder-role { font-size: 12.5px; font-weight: 700; color: #EF1D28; text-transform: uppercase; letter-spacing: 0.6px; margin-top: 4px; margin-bottom: 18px; }
    .ab-founder-quote { position: relative; margin: 0 0 24px; font-size: 15.5px; color: #374151; line-height: 1.8; font-style: italic; }
    .ab-founder-quote-mark { color: #FBD3D5; font-size: 32px; font-weight: 900; font-style: normal; line-height: 0; position: relative; top: 8px; margin-right: 2px; }
    .ab-founder-ctas { display: flex; gap: 10px; flex-wrap: wrap; }

    /* Departments */
    .ab-dept-grid { display: grid; grid-template-columns: repeat(5, minmax(0,1fr)); gap: 16px; }
    .ab-dept-card { background: white; border: 1px solid #E5E7EB; border-radius: 16px; padding: 22px 16px; text-align: center; transition: box-shadow 0.24s ease, transform 0.24s ease, border-color 0.24s ease; }
    .ab-dept-card:hover { box-shadow: 0 14px 36px rgba(15,23,42,0.07); transform: translateY(-3px); border-color: #D1D5DB; }
    .ab-dept-icon { width: 40px; height: 40px; border-radius: 11px; background: #FFF1F2; color: #EF1D28; display: flex; align-items: center; justify-content: center; margin: 0 auto 12px; }
    .ab-dept-label { font-size: 13px; font-weight: 800; color: #111827; margin-bottom: 6px; }
    .ab-dept-desc { margin: 0; font-size: 11.5px; color: #9CA3AF; line-height: 1.5; }

    /* Trust */
    .ab-trust-grid { display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 16px; max-width: 900px; margin: 0 auto; }
    .ab-trust-card { display: flex; align-items: center; gap: 12px; background: white; border: 1px solid #E5E7EB; border-radius: 14px; padding: 16px 18px; font-size: 13.5px; font-weight: 700; color: #111827; transition: border-color 0.2s ease, box-shadow 0.2s ease; }
    .ab-trust-card:hover { border-color: #D1D5DB; box-shadow: 0 8px 22px rgba(15,23,42,0.05); }
    .ab-trust-icon { width: 34px; height: 34px; border-radius: 10px; background: #ECFDF5; color: #059669; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }

    /* Roadmap */
    .ab-roadmap-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; max-width: 900px; margin: 0 auto; }
    .ab-roadmap-col { background: white; border: 1px solid #E5E7EB; border-radius: 20px; padding: 26px; }
    .ab-roadmap-col-head { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 800; margin-bottom: 18px; text-transform: uppercase; letter-spacing: 0.5px; }
    .ab-roadmap-now-head { color: #059669; }
    .ab-roadmap-next-head { color: #94A3B8; }
    .ab-roadmap-row { display: flex; align-items: center; gap: 10px; padding: 12px 4px; font-size: 14px; font-weight: 700; color: #111827; border-bottom: 1px solid #F3F4F6; }
    .ab-roadmap-row:last-child { border-bottom: none; }
    .ab-roadmap-row-icon { width: 28px; height: 28px; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
    .ab-roadmap-row-now .ab-roadmap-row-icon { background: #ECFDF5; color: #059669; }
    .ab-roadmap-row-next .ab-roadmap-row-icon { background: #F3F4F6; color: #94A3B8; }
    .ab-roadmap-row-next { color: #6B7280; }
    .ab-roadmap-live-badge { margin-left: auto; background: #ECFDF5; color: #059669; font-size: 10px; font-weight: 800; padding: 3px 9px; border-radius: 20px; text-transform: uppercase; letter-spacing: 0.4px; }
    .ab-roadmap-soon-badge { margin-left: auto; background: #F3F4F6; color: #9CA3AF; font-size: 10px; font-weight: 800; padding: 3px 9px; border-radius: 20px; text-transform: uppercase; letter-spacing: 0.4px; }

    /* FAQ */
    .ab-faq-list { display: flex; flex-direction: column; gap: 12px; max-width: 780px; margin: 0 auto; }
    .ab-faq-item { background: white; border: 1.5px solid #E5E7EB; border-radius: 16px; overflow: hidden; transition: border-color 0.25s ease, box-shadow 0.25s ease; }
    .ab-faq-item.open { border-color: #FECACA; box-shadow: 0 10px 30px rgba(239,29,40,0.08); }
    .ab-faq-trigger { width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 20px 24px; background: transparent; border: none; cursor: pointer; text-align: left; font-family: inherit; }
    .ab-faq-trigger span:first-child { font-size: 15.5px; font-weight: 700; color: #111827; line-height: 1.4; }
    .ab-faq-icon { position: relative; width: 30px; height: 30px; border-radius: 9px; background: #F9FAFB; border: 1px solid #E5E7EB; flex-shrink: 0; transition: background 0.25s ease, border-color 0.25s ease; }
    .ab-faq-item.open .ab-faq-icon { background: #FFF1F2; border-color: #FECDD3; }
    .ab-faq-icon .bar { position: absolute; top: 50%; left: 50%; background: #6B7280; border-radius: 2px; }
    .ab-faq-icon .bar.h { width: 12px; height: 2px; margin: -1px 0 0 -6px; }
    .ab-faq-icon .bar.v { width: 2px; height: 12px; margin: -6px 0 0 -1px; }
    .ab-faq-item.open .ab-faq-icon .bar { background: #EF1D28; }
    .ab-faq-answer { padding: 0 24px 22px; font-size: 14.5px; color: #6B7280; line-height: 1.8; }

    /* Final CTA */
    .ab-final-cta-outer { padding: 20px 48px 96px; }
    .ab-final-cta-card { max-width: 1100px; margin: 0 auto; background: linear-gradient(150deg, #060D1A 0%, #0D1120 50%, #0A1428 100%); border-radius: 28px; overflow: hidden; position: relative; padding: 64px 56px; border: 1px solid #1A2A3E; text-align: center; }
    .ab-final-cta-mesh { position: absolute; inset: 0; background-image: radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px); background-size: 28px 28px; pointer-events: none; }
    .ab-final-cta-glow { position: absolute; top: -100px; left: 50%; transform: translateX(-50%); width: 600px; height: 400px; background: radial-gradient(ellipse, rgba(239,29,40,0.18) 0%, transparent 65%); pointer-events: none; }
    .ab-final-cta-content { position: relative; max-width: 560px; margin: 0 auto; }
    .ab-final-cta-content h2 { margin: 0 0 14px; font-size: 34px; font-weight: 900; color: white; letter-spacing: -0.8px; line-height: 1.15; }
    .ab-final-cta-content p { margin: 0 0 30px; color: #94A3B8; font-size: 15px; line-height: 1.6; }
    .ab-final-cta-actions { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; }

    /* Responsive */
    @media (max-width: 1100px) {
      .ab-values-grid { grid-template-columns: repeat(2, minmax(0,1fr)); }
      .ab-stats-grid { grid-template-columns: repeat(3, minmax(0,1fr)); }
      .ab-dept-grid { grid-template-columns: repeat(3, minmax(0,1fr)); }
    }
    @media (max-width: 900px) {
      .ab-hero { padding: 64px 24px 80px; }
      .ab-hero-inner { flex-direction: column; gap: 44px; text-align: center; }
      .ab-hero-left { flex: none; }
      .ab-hero-left p { margin-left: auto; margin-right: auto; }
      .ab-hero-ctas { justify-content: center; }
      .ab-hero-left h1 { font-size: 36px; }
      .ab-section { padding: 56px 24px; }
      .ab-section-tint { padding-left: 24px; padding-right: 24px; }
      .ab-why-grid { grid-template-columns: 1fr; }
      .ab-mission-grid { grid-template-columns: 1fr; }
      .ab-roadmap-grid { grid-template-columns: 1fr; }
      .ab-founder-card { flex-direction: column; }
      .ab-founder-photo { flex: none; width: 100%; height: 260px; }
      .ab-eco-scroll { margin: 0 -24px; padding: 4px 24px 12px; }
      .ab-stats-outer { padding: 56px 24px; }
      .ab-final-cta-outer { padding: 12px 24px 72px; }
      .ab-final-cta-card { padding: 48px 28px; }
    }
    @media (max-width: 640px) {
      .ab-values-grid { grid-template-columns: 1fr; }
      .ab-stats-grid { grid-template-columns: 1fr 1fr; }
      .ab-dept-grid { grid-template-columns: 1fr 1fr; }
      .ab-trust-grid { grid-template-columns: 1fr; }
      .ab-why-solutions-grid { grid-template-columns: 1fr; }
      .ab-hero-left h1 { font-size: 30px; letter-spacing: -1px; }
      .ab-final-cta-content h2 { font-size: 26px; }
    }
  `}</style>
);
